import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { AccountState } from '@state/account/state';
import { TaskRole } from 'app/modules/settings/task-roles/task-roles.types';
import { catchError, map, Observable, of, Subject } from 'rxjs';
import { Task, TaskList, TaskQueryFilter, TaskResult } from '@tasks/tasks.types';
import { TaskState } from '@tasks/state/state';
import { TaskExportUtilsService } from '@tasks/services/task-export-utils.service';
import { Activity } from '@core/models';
import { TsSnackbarService } from 'tsui';
import { Cacheable } from 'ts-cacheable';
import { TaskRolesService } from 'app/modules/settings/task-roles/services/task-roles.service';

const taskRolesCache$ = new Subject<void>();

@Injectable({
  providedIn: 'root',
})
export class TasksService {
  private httpClient = inject(HttpClient);
  private accountId;
  private snackBar = inject(TsSnackbarService);

  constructor(
    private store: Store,
    private taskExportUtilsService: TaskExportUtilsService,
    private readonly taskRolesService: TaskRolesService,
  ) {
    this.accountId = this.store.selectSnapshot(AccountState.getAccountId);
  }

  // Get the base URL for the module
  // it will be removed when the backend is finished
  get apiBaseUrl() {
    return this.store.selectSnapshot(TaskState.getApiBaseUrl);
  }

  private get shallowUrl(): string {
    return 'tasks';
  }

  private buildTasksParams(taskListId?: number | string): Record<string, any> {
    const isAggregated = this.store.selectSnapshot(TaskState.isAggregatedView);
    const params: Record<string, any> = { account_id: this.accountId };
    if (!isAggregated && taskListId != null) {
      params['task_list_id'] = taskListId;
    }
    const isInRecordSavedView = this.store.selectSnapshot(TaskState.isInRecordAggregatedView);
    if (isInRecordSavedView) {
      const { relatedToType, relatedToTypeId } = this.store.selectSnapshot(TaskState.getTaskRelatedTo);
      params['related_to_id'] = relatedToTypeId;
      params['related_to_type'] = relatedToType;
    }
    return params;
  }

  createTask(taskListId: number, task: Task): Observable<Task> {
    task.workflow_state = task.workflow_state ?? 'not_started';
    const params = this.buildTasksParams(taskListId);

    return this.httpClient.post<any>(this.shallowUrl, { task }, { params }).pipe(map((response) => response.task));
  }

  @Cacheable({
    cacheBusterObserver: taskRolesCache$,
  })
  getTaskRolesByType(typeName?: string): Observable<TaskRole[]> {
    return this.taskRolesService.getByAccountId(this.accountId).pipe(
      map((task_roles) => {
        const taskRoles = typeName ? task_roles.filter((tr) => tr.applicable_type === typeName) : task_roles;
        return taskRoles;
      }),
    );
  }

  getTaskDetailById(taskListId: number, taskId: number, select: string[] = []): Observable<Task> {
    const params = this.buildTasksParams(taskListId);

    if (select && select.length > 0) {
      params['select'] = select.join(',');
    }

    return this.httpClient.get<{ task: Task }>(`${this.shallowUrl}/${taskId}`, { params }).pipe(
      map((response) => {
        return response.task;
      }),
    );
  }

  duplicateTask(taskListId: number, templateId: number, task: Task): Observable<Task> {
    const params = this.buildTasksParams(taskListId);
    return this.httpClient
      .post<any>(`${this.shallowUrl}/${task.id}/duplicate`, { task }, { params })
      .pipe(map((response: { task: Task }) => response.task));
  }

  updateTaskById(task: Task, templateId = undefined): Observable<Task> {
    const params = this.buildTasksParams(task.task_list_id);
    const sanitizedTask = this.sanitizeTaskPayload(task);

    return this.httpClient.put<any>(`${this.shallowUrl}/${task.id}`, { task: sanitizedTask }, { params }).pipe(
      map((response: { task: Task }) => response.task),
      catchError((error) => {
        throw error;
      }),
    );
  }

  bulkUpdateTasks(tasks): Observable<Task[]> {
    const params = this.buildTasksParams(tasks[0]?.task_list_id);
    return this.httpClient
      .put<any>(`task_bulk_action/update`, { tasks }, { params })
      .pipe(map((response) => response.tasks));
  }

  bulkDeleteTasks(tasks): Observable<Task[]> {
    const params = this.buildTasksParams(tasks[0]?.task_list_id);
    return this.httpClient.request<any>('delete', `task_bulk_action/delete`, { body: { tasks }, params });
  }

  bulkDuplicateTasks(tasks): Observable<Task[]> {
    const params = this.buildTasksParams(tasks[0]?.task_list_id);
    return this.httpClient
      .post<any>(`task_bulk_action/duplicate`, { tasks }, { params })
      .pipe(map((response) => response.tasks));
  }

  asyncBulkUpdateTask(tasks: Array<{ id: number | string; [key: string]: any }>): Observable<any> {
    const params = this.buildTasksParams(tasks[0]?.task_list_id);
    const payload = {
      task_bulk_action: {
        action_type: 'update',
        tasks: tasks.map((task) => ({
          id: task.id,
          ...task,
        })),
      },
    };
    return this.httpClient.post('task_bulk_action', payload, { params });
  }

  recalculateTaskDates(taskList: TaskList | { id: number }, templateId?: number): Observable<Task[]> {
    let url = `${this.apiBaseUrl}/task_lists/${taskList.id}/calculate_dates?account_id=${this.accountId}`;
    if (templateId) {
      url = `${url}&task_list_template_id=${templateId}`;
    }

    return this.httpClient.post<any>(url, {});
  }

  getTasks(
    taskListId: number,
    templateId?: number,
    filters: any = {},
    format: string = '',
    select: string = null,
  ): Observable<Task[]> {
    const params: any = {
      ...this.buildTasksParams(taskListId),
      format: format,
      group: filters.groupBy,
      sort: filters.sort,
      select: filters.select?.join(',') || '',
    };

    if (templateId) {
      params['task_list_template_id'] = templateId;
    }

    return this.httpClient.get<any>(this.shallowUrl, { params }).pipe(
      map((response) => {
        if (format && response && (format == 'csv' || format == 'pdf')) {
          if (response?.data?.message) {
            this.taskExportUtilsService.onExportResultWithMessage(response.data.message);
          }

          return response.data;
        }
        return response.tasks;
      }),
    );
  }

  deleteTask(taskListId: number, templateId: number, id: number, updateSubject = true): Observable<any> {
    const params = this.buildTasksParams(taskListId);
    return this.httpClient.delete<any>(`${this.shallowUrl}/${id}`, { params }).pipe(
      map((reponse) => {
        return reponse.task;
      }),
    );
  }

  updateTasksOrders(taskList: TaskList | { id: number }, tasks: Task[], templateId?: number): Observable<Task[]> {
    const params = this.buildTasksParams(taskList.id);
    if (templateId) {
      params['task_list_template_id'] = templateId;
    }

    let rows = [];
    for (let task of tasks) {
      rows.push({ task_id: task.id, rank: task.rank, parent_id: task.parent_id });
    }

    return this.httpClient.post<any>(`${this.shallowUrl}/update_order`, { tasks: rows }, { params }).pipe(
      map((response) => {
        return response.results;
      }),
    );
  }

  reorderTaskSection(taskListId: number, updateData: Task): Observable<any> {
    if (updateData.task_type === 'section') {
      return this.httpClient.post<any>(`accounts/${this.accountId}/task_list_sections/reorder`, {
        task_list_id: taskListId,
        task_section_id: updateData.task_section_id,
        rank: updateData.rank,
      });
    }

    // Subtask PUT direct
    if (updateData.parent_id != null) {
      return this.updateTaskById({
        id: updateData.id,
        task_list_id: taskListId,
        rank: updateData.rank,
      } as Task);
    }

    return this.httpClient.post<any>(`accounts/${this.accountId}/task_section_tasks/reorder`, {
      task_list_id: taskListId,
      task_section_id: updateData.task_section_id,
      task_id: updateData.id,
      rank: updateData.rank,
    });
  }

  unlinkTask(id: number): Observable<any> {
    return this.httpClient.delete<Activity>(`linked_objects/${id}`).pipe(
      map((response) => {
        return response;
      }),
    );
  }

  search(filter: TaskQueryFilter): Observable<{ count?: number; tasks?: Task[]; data?: { message?: string } }> {
    const params = this.buildTasksParams(filter.task_list_id);

    return this.httpClient.post(`${this.shallowUrl}/search`, filter, { params }).pipe(
      map((response: TaskResult & { data: { message: string } }) => {
        if (filter.format && response) {
          this.snackBar.open(response.data.message, 'info');
          return response;
        }
        return {
          count: response.count,
          // include all task_type in the search result filter.
          tasks: response.results.filter(
            ({ task_type }) =>
              ['task', 'section', 'key_date'].includes(task_type) ||
              (filter?.include_keydates ? task_type === 'key_date' : false),
          ),
        };
      }),
      catchError((error) => {
        if (filter.format) {
          this.snackBar.open('Error while exporting', 'error');
        }
        return of({ count: 0, tasks: [] });
      }),
    );
  }

  importFile(taskListId: number, document: any): Observable<Task[]> {
    const uploadData = new FormData();
    uploadData.append('file', document);

    let url = `${this.apiBaseUrl}/task_lists/${taskListId}/import_file?account_id=${this.accountId}`;

    return this.httpClient.post<any>(url, uploadData).pipe(
      map((response) => {
        console.log('response', response);
        return response.results;
      }),
    );
  }
  private sanitizeTaskPayload(task: Task): Task {
    const cloned: any = { ...task };

    Object.keys(cloned).forEach((key) => {
      const value = cloned[key];
      if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed === '|') {
          cloned[key] = null;
        }
      } else if (Array.isArray(value)) {
        if (value.some((v) => typeof v === 'string')) {
          cloned[key] = value.map((v) => (typeof v === 'string' && v.trim() === '|' ? null : v));
        }
      }
    });

    if (Array.isArray(cloned.custom_field_values)) {
      cloned.custom_field_values = cloned.custom_field_values.map((cf: any) => {
        if (cf && typeof cf.val === 'string' && cf.val.trim() === '|') {
          return { ...cf, val: null };
        }
        return cf;
      });
    }

    return cloned as Task;
  }
}
